import { I18n } from "@assets/strings";
import React, { useState } from "react";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { useServices } from "@core/servicesContext";
import { useOrganizationAlias } from "@domain/currentProfile/hooks";
import { useTrack } from "@domain/organization/track/hooks";
import { DraftRenderer } from "@ui/components/containers/draftRenderer";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { Title } from "@ui/components/containers/title";
import { Row, row, stack } from "@ui/components/layout";
import { KebabMenu } from "@ui/components/menus/kebabMenu";
import { SimpleMenuContent } from "@ui/components/menus/simpleMenuContent";
import { StickyColumn } from "@ui/components/stickyColumn";
import { useModalTools } from "@ui/modals/modalContext";
import { desktop, gap, mobile } from "@ui/styles/utils";
import { DiplomaEditorModal } from "./diplomaEditorModal";
import { DiplomaInfos } from "./diplomaInfos";
import { TrackLevelView } from "./trackLevelView";

interface TrackDetailsProps extends React.HTMLAttributes<HTMLDivElement> {
	trackId: string;
}
export const TrackDetails: React.FC<TrackDetailsProps> = ({ trackId, ...htmlProps }) => {
	const organizationAlias = useOrganizationAlias();
	const [selectedDiploma, setSelectedDiploma] = useState<{ levelId: string; diplomaId: string } | null>(null);
	const { trackService } = useServices();
	const { loading, result: track } = useTrack(trackId);

	const { createModal } = useModalTools();

	if (!track) {
		return loading ? <ClipLoader /> : null;
	}
	const displayedDiploma = selectedDiploma
		? track.levels
				.find((lv) => lv.id === selectedDiploma.levelId)
				?.diplomas.find((diploma) => diploma.id === selectedDiploma?.diplomaId)
		: null;

	return (
		<Container {...htmlProps}>
			<Levels>
				<PresentationLevel onClick={() => setSelectedDiploma(null)}>
					{I18n.t("organization.track.presentation")}
				</PresentationLevel>
				{track.levels.map((level) => (
					<TrackLevelView
						track={track}
						key={level.id}
						level={level}
						onDiplomaSelected={(diploma) => setSelectedDiploma({ levelId: level.id, diplomaId: diploma.id })}
					/>
				))}
			</Levels>
			<StyledStickyColumn topOffset={100}>
				<TrackPresentation>
					<Row gap={10} justify="space-between">
						<Title>
							<b>{displayedDiploma?.title ?? track.name}</b>
						</Title>
						{organizationAlias && selectedDiploma && displayedDiploma && (
							<KebabMenu>
								<SimpleMenuContent
									onEdit={() =>
										createModal((close) => (
											<DiplomaEditorModal
												close={close}
												track={track}
												levelId={selectedDiploma.levelId}
												initialDiploma={displayedDiploma}
											/>
										))
									}
									onDelete={() => trackService.deleteDiploma(track, selectedDiploma.levelId, selectedDiploma.diplomaId)}
								/>
							</KebabMenu>
						)}
					</Row>
					{displayedDiploma && <DiplomaInfos diploma={displayedDiploma} />}
					<DraftRenderer content={displayedDiploma?.description ?? track.description} />
				</TrackPresentation>
			</StyledStickyColumn>
		</Container>
	);
};

const Container = styled.div`
	@media ${desktop} {
		${row(20)}
	}
	@media ${mobile} {
		${stack(40, "flex-start", "stretch")};
	}
`;

const Levels = styled.div`
	@media ${desktop} {
		width: 280px;
	}
	flex-shrink: 0;
	flex-grow: 0;
	${gap(20)};
	@media ${mobile} {
		${gap(10)};
	}
`;

const PresentationLevel = styled(RoundedBlock)`
	cursor: pointer;
	padding: 15px 35px;
	@media ${mobile} {
		padding: 15px 20px;
	}
`;

const TrackPresentation = styled(RoundedBlock)`
	${gap(20)};
	position: relative;
	padding: 40px;
`;

const StyledStickyColumn = styled(StickyColumn)`
	flex: 1;
`;
